// Which schemas' faxes the inbox shows.
//
// Mirrors the Providers dropdown, with one deliberate difference: this defaults
// to ALL schemas in the clinic group, not just home. An admin opening the fax
// inbox is triaging for the practice — a fax that landed on a colleague's
// number is still theirs to route — whereas the Providers dropdown filters a
// clinician's own working lists.
//
// Selection is persisted per user, and stored as an EXPLICIT list rather than
// "all" so a deliberate narrowing survives a reload. A schema that later leaves
// the group drops out on read.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelectedProviders } from "./useSelectedProviders";

const KEY = "vmai:fax-inbox-schemas";

function readStored() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null; // absent, blocked or corrupt — treat as "never chosen"
  }
}

function persist(list) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch (_) { /* the session still works without persistence */ }
}

/**
 * @returns {{allSchemas, selected, isSelected, toggle, selectAll, clearToHome, homeSchema}}
 */
export function useFaxInboxSchemas() {
  const { allSchemas, homeSchema } = useSelectedProviders();
  // null = the user has never chosen; that is what "default to all" keys on,
  // and it must stay distinct from an empty selection.
  const [chosen, setChosen] = useState(readStored);

  // Once the group loads, an untouched inbox adopts all of it.
  const selected = useMemo(() => {
    const group = allSchemas || [];
    if (!chosen) return group;
    const valid = chosen.filter((s) => group.includes(s));
    // Never leave the inbox showing nothing at all: an empty or fully-stale
    // selection falls back to home rather than an empty page.
    return valid.length ? valid : [homeSchema].filter(Boolean);
  }, [chosen, allSchemas, homeSchema]);

  // Drop schemas that have left the group, so a stale entry can't linger in
  // storage forever and reappear if the group changes back.
  useEffect(() => {
    if (!chosen || !allSchemas?.length) return;
    const valid = chosen.filter((s) => allSchemas.includes(s));
    if (valid.length !== chosen.length) {
      setChosen(valid);
      persist(valid);
    }
  }, [chosen, allSchemas]);

  const commit = useCallback((list) => {
    setChosen(list);
    persist(list);
  }, []);

  const toggle = useCallback((schema) => {
    const next = selected.includes(schema)
      ? selected.filter((s) => s !== schema)
      : [...selected, schema];
    // Unticking the last one would blank the inbox with no way back except the
    // dropdown you just emptied; keep at least one.
    commit(next.length ? next : selected);
  }, [selected, commit]);

  const selectAll = useCallback(() => commit(allSchemas || []), [allSchemas, commit]);
  const clearToHome = useCallback(
    () => commit([homeSchema].filter(Boolean)), [homeSchema, commit]);

  const isSelected = useCallback((s) => selected.includes(s), [selected]);

  return { allSchemas: allSchemas || [], homeSchema, selected, isSelected,
           toggle, selectAll, clearToHome };
}
